import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuthStore } from '../store';
import { ArrowLeft, Crown, FileText, Plus, Trash2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPES = [
  { v: 'irpf', l: 'IRPF (Imposto de Renda Pessoa Física)' },
  { v: 'memorando', l: 'Memorando / Manifestação' },
  { v: 'distribuicao_lucros', l: 'Distribuição de Lucros' },
  { v: 'procuracao', l: 'Procuração' },
  { v: 'outro', l: 'Outro' },
];

const DOC_LABEL = Object.fromEntries(DOC_TYPES.map(d => [d.v, d.l]));

export default function PayrollPartnersPage() {
  const navigate = useNavigate();
  const { token, tenant } = useAuthStore();
  const headers = useMemo(() => ({
    Authorization: `Bearer ${token}`,
    'x-tenant-slug': tenant?.slug,
  }), [token, tenant]);

  const [partners, setPartners] = useState([]);
  const [selected, setSelected] = useState(null);
  const [docs, setDocs] = useState([]);
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [showAddPartner, setShowAddPartner] = useState(false);
  const [partnerForm, setPartnerForm] = useState({ name: '', role: '', cpf: '', notes: '' });
  const [docForm, setDocForm] = useState({ doc_type: 'irpf', title: '', year_ref: new Date().getFullYear() - 1, file_url: '', notes: '' });

  const submitPartner = async () => {
    if (!partnerForm.name || !partnerForm.role) { toast.error('Nome e cargo obrigatórios'); return; }
    try {
      await axios.post(`${API}/api/payroll/partners`, partnerForm, { headers });
      toast.success('Sócio cadastrado');
      setShowAddPartner(false);
      setPartnerForm({ name: '', role: '', cpf: '', notes: '' });
      fetchPartners();
    } catch (e) {
      toast.error('Erro ao salvar sócio');
    }
  };

  const fetchPartners = async () => {
    const r = await axios.get(`${API}/api/payroll/partners`, { headers });
    setPartners(r.data || []);
  };

  const openPartner = async (p) => {
    setSelected(p);
    const r = await axios.get(`${API}/api/payroll/partners/${p.id}/documents`, { headers });
    setDocs(r.data || []);
  };

  const submitDoc = async () => {
    if (!docForm.title) { toast.error('Informe o título'); return; }
    try {
      await axios.post(`${API}/api/payroll/partners/${selected.id}/documents`, { ...docForm, partner_id: selected.id }, { headers });
      toast.success('Documento adicionado');
      setShowAddDoc(false);
      setDocForm({ doc_type: 'irpf', title: '', year_ref: new Date().getFullYear() - 1, file_url: '', notes: '' });
      openPartner(selected);
      fetchPartners();
    } catch (e) {
      toast.error('Erro ao salvar documento');
    }
  };

  const removeDoc = async (docId) => {
    if (!window.confirm('Remover este documento?')) return;
    await axios.delete(`${API}/api/payroll/partners/${selected.id}/documents/${docId}`, { headers });
    openPartner(selected);
    fetchPartners();
  };

  useEffect(() => { fetchPartners(); }, []);

  return (
    <div className="p-6 max-w-[1100px] mx-auto space-y-5">
      <Button variant="ghost" size="sm" onClick={() => navigate('/rh/folha')} data-testid="back-btn">
        <ArrowLeft className="h-4 w-4 mr-1" /> Voltar
      </Button>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Crown className="h-6 w-6 text-violet-600" /> Sócios & Documentos Pessoais
          </h1>
          <p className="text-sm text-slate-500 mt-1">Espaço pessoal dos sócios — IRPF, memorandos, procurações, distribuição de lucros.</p>
        </div>
        {!selected && (
          <Dialog open={showAddPartner} onOpenChange={setShowAddPartner}>
            <DialogTrigger asChild>
              <Button data-testid="add-partner-btn"><Plus className="h-4 w-4 mr-1" /> Novo Sócio</Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>Cadastrar novo sócio</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <label className="text-xs uppercase text-slate-500">Nome *</label>
                  <Input value={partnerForm.name} onChange={(e) => setPartnerForm({ ...partnerForm, name: e.target.value })} placeholder="Ex: Fabio Tomaselli" data-testid="partner-name" />
                </div>
                <div>
                  <label className="text-xs uppercase text-slate-500">Cargo / Tipo *</label>
                  <Input value={partnerForm.role} onChange={(e) => setPartnerForm({ ...partnerForm, role: e.target.value })} placeholder="Ex: Sócio Pró-Labore" data-testid="partner-role" />
                </div>
                <div>
                  <label className="text-xs uppercase text-slate-500">CPF</label>
                  <Input value={partnerForm.cpf} onChange={(e) => setPartnerForm({ ...partnerForm, cpf: e.target.value })} placeholder="opcional" />
                </div>
                <div>
                  <label className="text-xs uppercase text-slate-500">Observações</label>
                  <Input value={partnerForm.notes} onChange={(e) => setPartnerForm({ ...partnerForm, notes: e.target.value })} placeholder="opcional" />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setShowAddPartner(false)}>Cancelar</Button>
                  <Button onClick={submitPartner} data-testid="submit-partner">Salvar</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Lista de sócios */}
      {!selected ? (
        <div className="grid md:grid-cols-2 gap-4">
          {partners.map(p => (
            <Card key={p.id} className="hover:shadow-md cursor-pointer transition-shadow" onClick={() => openPartner(p)} data-testid={`partner-${p.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">{p.name}</h3>
                    <p className="text-sm text-violet-700">{p.role}</p>
                    <p className="text-xs text-slate-500 mt-2">{p.notes}</p>
                  </div>
                  <Badge variant="outline" className="border-violet-300 text-violet-700">
                    {p.documents_count} doc{p.documents_count !== 1 ? 's' : ''}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <Button variant="ghost" size="sm" onClick={() => setSelected(null)} className="mb-2 -ml-2">
                <ArrowLeft className="h-4 w-4 mr-1" /> {selected.name}
              </Button>
              <CardTitle className="text-base">{selected.role}</CardTitle>
            </div>
            <Dialog open={showAddDoc} onOpenChange={setShowAddDoc}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="add-doc-btn"><Plus className="h-4 w-4 mr-1" /> Novo Documento</Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle>Adicionar documento</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs uppercase text-slate-500">Tipo</label>
                    <Select value={docForm.doc_type} onValueChange={(v) => setDocForm({ ...docForm, doc_type: v })}>
                      <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {DOC_TYPES.map(d => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs uppercase text-slate-500">Título</label>
                    <Input value={docForm.title} onChange={(e) => setDocForm({ ...docForm, title: e.target.value })} placeholder="Ex: Memorando IRPF AC2025" data-testid="doc-title" />
                  </div>
                  <div>
                    <label className="text-xs uppercase text-slate-500">Ano de referência</label>
                    <Input type="number" value={docForm.year_ref} onChange={(e) => setDocForm({ ...docForm, year_ref: Number(e.target.value) })} />
                  </div>
                  <div>
                    <label className="text-xs uppercase text-slate-500">URL do arquivo (opcional)</label>
                    <Input value={docForm.file_url} onChange={(e) => setDocForm({ ...docForm, file_url: e.target.value })} placeholder="https://..." />
                  </div>
                  <div>
                    <label className="text-xs uppercase text-slate-500">Observações</label>
                    <Input value={docForm.notes} onChange={(e) => setDocForm({ ...docForm, notes: e.target.value })} />
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={() => setShowAddDoc(false)}>Cancelar</Button>
                    <Button onClick={submitDoc} data-testid="submit-doc">Salvar</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {docs.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <FileText className="h-12 w-12 mx-auto mb-2 opacity-30" />
                <p>Nenhum documento ainda</p>
              </div>
            ) : (
              <div className="space-y-2">
                {docs.map(d => (
                  <div key={d.id} className="flex items-start justify-between p-3 border rounded hover:bg-slate-50">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">{DOC_LABEL[d.doc_type] || d.doc_type}</Badge>
                        {d.year_ref && <span className="text-xs text-slate-500">{d.year_ref}</span>}
                      </div>
                      <div className="font-medium text-slate-900 mt-1">{d.title}</div>
                      {d.notes && <div className="text-xs text-slate-500 mt-0.5">{d.notes}</div>}
                    </div>
                    <div className="flex gap-1">
                      {d.file_url && (
                        <a href={d.file_url} target="_blank" rel="noreferrer">
                          <Button size="sm" variant="ghost"><ExternalLink className="h-4 w-4" /></Button>
                        </a>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => removeDoc(d.id)} data-testid={`del-doc-${d.id}`}>
                        <Trash2 className="h-4 w-4 text-rose-600" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
